import {
    Dialog,
    DialogContent,
    IconButton,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    Zoom,
} from "@mui/material";
import React, {createContext, ReactNode, useCallback, useContext, useMemo, useState} from "react";
import CloseIcon from "@mui/icons-material/Close";
import {EmployeeRef, KeySnapshot} from "../../api/Model";
import {KeyChip} from "../../components/key-chip/KeyChip";
import {KeyTagChip} from "../../components/key-tag-chip/KeyTagChip";
import EmployeeLink from "../../components/employee-link/EmployeeLink";
import EmployeeAvatar from "../../components/employee-avatar/EmployeeAvatar";
import {HierarchyEmployee} from "../../util/EmployeeHierarchy";
import {SiteChip} from "../../components/site-chip/SiteChip";
import TeamComponent from "../../components/team-component/TeamComponent";
import {KeyHistoryRecordAudit} from "../../components/key-history-record-audit/KeyHistoryRecordAudit";
import {combineCmp, stringCmp} from "../../util/SortUtil";
import {KeyHistoryPeriod} from "../../components/key-history-period/KeyHistoryPeriod";
import {useMessage} from "../../util/actions/Message";
import {useEmployeeKeyHistoryStore} from "../../api/EmployeeKeyHistoryStore";
import {useEmployeeNeighbourhoodStore} from "../../api/EmployeeNeighbourhoodStore";
import {useEmployeeHierarchyStore} from "../../api/EmployeeHierarchyStore";
import {
    EmployeeKeyHistoryDialogContext,
    KeyHistoryButton,
} from "../history-dialog/HistoryDialogContext";

function EmployeeKeyHistoryRecordRow({snapshot}: {
    snapshot: KeySnapshot,
}) {
    const {key: keyData, historyRecord: record} = snapshot;

    return (
        <TableRow>
            <TableCell sx={{fontWeight: record.to ? undefined : 'bold'}}>
                <KeyHistoryPeriod record={record}/>
            </TableCell>
            <TableCell>
                <Stack direction="row" alignItems="center" gap={3} sx={{flex: 1}} flexWrap="wrap">
                    <KeyChip keyData={keyData} record={record}/>
                    {keyData.tags.map(tag => (
                        <KeyTagChip keyTag={tag} key={tag.id} size="small"/>
                    ))}
                    <KeyHistoryButton keyData={keyData}/>
                </Stack>
            </TableCell>
            <TableCell><KeyHistoryRecordAudit record={record}/></TableCell>
            <TableCell>{snapshot.historyRecord.notes}</TableCell>
        </TableRow>
    );
}

export function EmployeeKeyHistoryDialog({employee, history, open, onClose}: {
    employee: HierarchyEmployee,
    history: KeySnapshot[],
    open: { open: boolean, x: number; y: number },
    onClose: () => void,
}) {
    const sortedHistory = useMemo(() => [...history].sort(
        combineCmp<KeySnapshot>(
            stringCmp(it => it.historyRecord.to || '9999-99-99', true),
            stringCmp(it => it.historyRecord.from || '0000-00-00', true),
            stringCmp(it => it.historyRecord.createdAt || '', true),
        )
    ), [history]);

    return (
        <Dialog
            open={open.open}
            onClose={onClose}
            fullWidth
            maxWidth="xl"
            slots={{
                transition: Zoom,
            }}
            slotProps={{
                transition: {
                    style: {transformOrigin: `${open.x}px ${open.y}px`},
                    timeout: {enter: 300, exit: 300},
                    easing: {enter: "ease-out", exit: "ease-in"},
                },
            }}
        >
            <DialogContent>
                <Stack direction="row" gap={4} alignItems="center" justifyContent="space-between" mb={2}>
                    <Stack direction="row" gap={4} alignItems="center">
                        <Typography variant="h6">
                            Keys of
                        </Typography>
                        <Stack direction="row" gap={1} alignItems="center">
                            <EmployeeAvatar employee={employee} sx={{width: 32, height: 32}}/>
                            <EmployeeLink employee={employee}/>
                            <Typography variant="inherit">
                                {employee.jobTitle}
                            </Typography>
                            at
                            {employee.teamPath && (
                                <TeamComponent
                                    teamPath={employee.teamPath}
                                    slotProps={{content: {sx: {fontSize: 'inherit'}}, wrapper: {width: 'auto'}}}
                                />
                            )}
                            <SiteChip employee={employee}/>
                        </Stack>
                    </Stack>
                    <IconButton onClick={onClose}>
                        <CloseIcon/>
                    </IconButton>
                </Stack>

                <TableContainer component={Paper} variant="outlined">
                    <Table size="medium">
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{fontWeight: 'bold'}} width="240px">Dates</TableCell>
                                <TableCell sx={{fontWeight: 'bold'}} width="320px">Key</TableCell>
                                <TableCell sx={{fontWeight: 'bold'}} width="320px">Recorded&nbsp;by</TableCell>
                                <TableCell sx={{fontWeight: 'bold'}}>Notes</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {sortedHistory.map(snapshot => (
                                <EmployeeKeyHistoryRecordRow
                                    key={`${snapshot.key.id}-${snapshot.historyRecord.id}`}
                                    snapshot={snapshot}
                                />
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </DialogContent>
        </Dialog>
    );
}

interface EmployeeKeyHistoryDialogState {
    x: number;
    y: number;
    employee: HierarchyEmployee;
    history: KeySnapshot[];
}

interface EmployeeKeyHistoryDialogRendererContextType {
    dialogState: EmployeeKeyHistoryDialogState | null;
    handleCloseDialog: () => void;
}

const EmployeeKeyHistoryDialogRendererContext = createContext<EmployeeKeyHistoryDialogRendererContextType | null>(null);

export function EmployeeKeyHistoryDialogProvider({children}: {children: ReactNode}) {
    const message = useMessage();
    const employeeKeyHistory = useEmployeeKeyHistoryStore(state => state.remoteValue);
    const employeeNeighbourhoods = useEmployeeNeighbourhoodStore(state => state.remoteValue);
    const hierarchy = useEmployeeHierarchyStore(employeeNeighbourhoods);
    const [dialogState, setDialogState] = useState<EmployeeKeyHistoryDialogState | null>(null);

    const openEmployeeKeyHistoryDialog = useCallback(async (
        employeeRef: EmployeeRef,
        origin: {x: number, y: number},
    ) => {
        try {
            const [resolvedHierarchy, resolvedEmployeeKeyHistory] = await Promise.all([
                hierarchy.value,
                employeeKeyHistory.value,
            ]);
            const employee = resolvedHierarchy.employees.find(employee => employee.id === employeeRef.id);
            if (employee) {
                setDialogState({
                    ...origin,
                    employee,
                    history: resolvedEmployeeKeyHistory.get(employeeRef),
                });
            } else {
                message.error('Failed to load employee key history. Please try again later.');
            }
        } catch (e) {
            message.error('Failed to load employee key history. Please try again later.', e);
        }
    }, [employeeKeyHistory, hierarchy, message]);

    const handleCloseDialog = useCallback(() => setDialogState(null), []);
    const contextValue = useMemo(() => ({openEmployeeKeyHistoryDialog}), [openEmployeeKeyHistoryDialog]);
    const rendererContextValue = useMemo(
        () => ({dialogState, handleCloseDialog}),
        [dialogState, handleCloseDialog],
    );

    return (
        <EmployeeKeyHistoryDialogContext value={contextValue}>
            <EmployeeKeyHistoryDialogRendererContext value={rendererContextValue}>
                {children}
            </EmployeeKeyHistoryDialogRendererContext>
        </EmployeeKeyHistoryDialogContext>
    );
}

export function EmployeeKeyHistoryDialogRenderer() {
    const context = useContext(EmployeeKeyHistoryDialogRendererContext);
    if (!context) {
        throw new Error('EmployeeKeyHistoryDialogRenderer must be used within an EmployeeKeyHistoryDialogProvider');
    }
    const {dialogState, handleCloseDialog} = context;

    if (!dialogState) return null;

    return (
        <EmployeeKeyHistoryDialog
            employee={dialogState.employee}
            history={dialogState.history}
            open={{open: true, x: dialogState.x, y: dialogState.y}}
            onClose={handleCloseDialog}
        />
    );
}
